import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Aperture, Shield, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import PageShell from "@/components/PageShell";
import SectionTitle from "@/components/SectionTitle";
import { useSEO } from "@/hooks/useSEO";
import { useMarketingImage } from "@/hooks/useMarketingImage";

const DEFAULT_STUDIO_IMAGES = [
  "https://images.pexels.com/photos/17345669/pexels-photo-17345669.jpeg",
  "https://images.pexels.com/photos/38055414/pexels-photo-38055414.jpeg",
  "https://images.pexels.com/photos/35247377/pexels-photo-35247377.jpeg",
];

const About = () => {
  const { t, lang } = useLanguage();
  // Marketing images — admin can override via /admin → Images marketing.
  // Slot 0 is shared with the home page studio section.
  const STUDIO_IMAGES = [
    useMarketingImage("home.studio", DEFAULT_STUDIO_IMAGES[0]),
    useMarketingImage("about.studio_2", DEFAULT_STUDIO_IMAGES[1]),
    useMarketingImage("about.studio_3", DEFAULT_STUDIO_IMAGES[2]),
  ];
  useSEO({
    title: t("about_page.title"),
    description: t("about_page.body_1"),
    image: STUDIO_IMAGES[0],
    lang,
  });

  const values = [
    {
      icon: Shield,
      title: t("about_page.v1_title"),
      body: t("about_page.v1_body"),
    },
    {
      icon: Aperture,
      title: t("about_page.v2_title"),
      body: t("about_page.v2_body"),
    },
    {
      icon: Sparkles,
      title: t("about_page.v3_title"),
      body: t("about_page.v3_body"),
    },
  ];

  return (
    <PageShell dataTestId="about-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-12">
        <SectionTitle
          overline={t("about_page.overline")}
          title={t("about_page.title")}
          testId="about-title"
        />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="md:col-span-6 kw-img-frame aspect-[4/5]"
          >
            <img src={STUDIO_IMAGES[0]} alt="Studio" />
          </motion.div>

          <div className="md:col-span-6 space-y-6 md:pt-10">
            <p className="text-lg md:text-xl text-[#F2EBE5] leading-relaxed italic font-serif-display">
              « {t("about_page.body_1")} »
            </p>
            <p className="text-base text-[#A19E9C] leading-relaxed">
              {t("about_page.body_2")}
            </p>
            <p className="text-base text-[#A19E9C] leading-relaxed">
              {t("about_page.body_3")}
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <p className="overline kw-accent-line mb-10">
          {t("about_page.values_title")}
        </p>
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {values.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                data-testid={`value-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="border border-[#F2EBE5]/10 p-8 md:p-10 hover:border-[#C5A059]/40 transition-colors bg-[#0a0a0a]"
              >
                <Icon className="w-7 h-7 text-[#C5A059] mb-8" strokeWidth={1.25} />
                <h3 className="font-serif-display text-2xl mb-3">{v.title}</h3>
                <p className="text-sm text-[#A19E9C] leading-relaxed">{v.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Image band */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24">
        <div className="grid grid-cols-2 gap-4 md:gap-6">
          <div className="kw-img-frame aspect-[4/3]">
            <img src={STUDIO_IMAGES[1]} alt="Studio" />
          </div>
          <div className="kw-img-frame aspect-[4/3]">
            <img src={STUDIO_IMAGES[2]} alt="Studio" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-28 border-t border-[#C5A059]/20 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <h3 className="font-serif-display text-3xl md:text-5xl">
            {t("home.cta_title")}
          </h3>
          <Link to="/contact" data-testid="about-cta-button" className="kw-btn-primary">
            {t("nav.order")}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
};

export default About;
